import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { File } from '@app/core/interfaces/download/file';
import { Hyperlink } from '@app/core/interfaces/hyperlink';
import { ResourceType } from '@app/core/interfaces/portal/resource-type';
import { FileRepository } from '@app/core/repositories/file.repository';
import { NotificationService } from '@app/core/services/notification.service';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import {
  DownloadProgressEvent,
  FileDownloadService,
  HttpProgressEventType,
} from '../../services/file-download.service';

/**
 * Downloading a file over HTTP with progress indicator on the button.
 *
 * ```html
 * <portal-file-download
 *  url="observable for the URL"
 *  disabled="boolean expression"
 *  btnClass="some bootstrap semantic style class, e.g. btn-primary"
 *  progressText="expression providing value that will be shown instead of the spinner icon"
 *  result="emits the result of the operation. `Blob` on download, or `Error` if operation was unsuccessful"
 *  [isNgbDropdownItem]"if used in dropdown set to `true`"
 * >custom html here</portal-file-download>
 * ```
 */
@Component({
  selector: 'portal-file-download',
  templateUrl: './file-download.component.html',
  styles: [],
  standalone: false,
})
export class FileDownloadComponent implements OnChanges {
  @Input()
  btnClass: string = 'btn-outline-secondary';

  @Input()
  disabled: boolean = false;

  @Input()
  file?: File;

  /**
   * Setting this will render a hyperlink HTML element instead of the button.
   */
  @Input()
  hyperlink: Hyperlink;

  @Input()
  isNgbDropdownItem: boolean = false;

  @Input()
  progressText: string;

  @Input()
  showProgress: boolean = true;

  @Input()
  url: string;

  @Output()
  result: EventEmitter<any> = new EventEmitter();

  progress: number;

  private errorMessage: string;
  private logger = loggerFor('FileDownloadComponent');

  constructor(
    private filesRepository: FileRepository,
    private service: FileDownloadService,
    private notificationService: NotificationService,
    translateService: TranslateService
  ) {
    this.errorMessage = translateService.instant('file-download.error');
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('file' in changes && changes.file.currentValue != null) {
      this.setFromFile(changes.file.currentValue);
    }
  }

  private setFromFile(file: File) {
    if (file.resourceType === ResourceType.EXT_URL) {
      this.hyperlink = {
        href: file.url,
        target: '_blank',
      };
    } else if (file.resourceType === ResourceType.FILE) {
      this.url = this.filesRepository.downloadApiUrl(file.id!);
    } else if (file.resourceType === ResourceType.EXT_DOWNLOAD) {
      this.url = file.url;
    }
  }

  public download(): void {
    this.service.download(this.url).subscribe(
      (event: DownloadProgressEvent) => {
        switch (event.type) {
          case HttpProgressEventType.Start:
            this.disabled = true;
            this.updateProgress(0);
            break;
          case HttpProgressEventType.Progress:
            this.updateProgress(event.done);
            break;
          case HttpProgressEventType.Done:
            this.updateProgress(undefined);
            this.disabled = false;
            saveAs(event.data, event.fileName);
            this.result.emit(event.data);
        }
      },
      (err: Error) => {
        this.logger.error(err);
        this.result.emit(err);
        this.notificationService.showDanger(this.errorMessage);
        this.updateProgress(undefined);
        this.disabled = false;
      }
    );
  }

  private updateProgress(progress: number) {
    if (this.showProgress === true) {
      this.progress = progress;
    }
  }
}
